// ── Status ────────────────────────────────────────────────────────────────────

export type ReconciliationStatus = 'Draft' | 'Submitted' | 'Completed';

export type ReconciliationTransactionType =
  | 'Deposit'
  | 'Withdrawal'
  | 'Charge'
  | 'Interest'
  | 'Transfer'
  | 'Other';

// ── Display helpers ────────────────────────────────────────────────────────────

export const RECONCILIATION_STATUS_LABELS: Record<ReconciliationStatus, string> = {
  Draft:     'Draft',
  Submitted: 'Submitted',
  Completed: 'Completed',
};

export const RECONCILIATION_STATUS_COLORS: Record<ReconciliationStatus, string> = {
  Draft:     'bg-amber-100 text-amber-700',
  Submitted: 'bg-blue-100 text-blue-700',
  Completed: 'bg-green-100 text-green-700',
};

export const RECONCILIATION_TXTYPE_LABELS: Record<ReconciliationTransactionType, string> = {
  Deposit:    'Deposit',
  Withdrawal: 'Withdrawal',
  Charge:     'Bank Charge',
  Interest:   'Interest',
  Transfer:   'Transfer',
  Other:      'Other',
};

export const RECONCILIATION_TXTYPE_COLORS: Record<ReconciliationTransactionType, string> = {
  Deposit:    'text-green-600',
  Withdrawal: 'text-red-600',
  Charge:     'text-orange-600',
  Interest:   'text-blue-600',
  Transfer:   'text-purple-600',
  Other:      'text-slate-600',
};

// ── Line DTO ──────────────────────────────────────────────────────────────────

export interface BankReconciliationLineDto {
  id:                string;
  reconciliationId:  string;
  lineNumber:        number;
  transactionType:   ReconciliationTransactionType;
  transactionId?:    string | null;
  transactionNumber: string;
  transactionDate:   string;
  description?:      string | null;
  amount:            number;
  isCleared:         boolean;
  clearedDate?:      string | null;
  remarks?:          string | null;
}

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface BankReconciliationDto {
  id:                      string;
  companyId:               string;
  locationId?:             string | null;
  financialYearCode:       string;
  financialPeriodCode:     string;
  reconciliationNumber:    string;
  bankAccountId:           string;
  bankAccountCode:         string;
  bankAccountName:         string;
  bankGLAccountId?:        string | null;
  currencyCode:            string;
  statementDate:           string;
  statementOpeningBalance: number;
  statementClosingBalance: number;
  bookOpeningBalance:      number;
  clearedCredits:          number;
  clearedDebits:           number;
  bookClosingBalance:      number;
  unclearedCredits:        number;
  unclearedDebits:         number;
  difference:              number;
  status:                  ReconciliationStatus;
  notes?:                  string | null;
  completedBy?:            string | null;
  completedDate?:          string | null;
  reopenedBy?:             string | null;
  reopenedDate?:           string | null;
  reopenReason?:           string | null;
  createdDate:             string;
  modifiedDate?:           string | null;
  lines:                   BankReconciliationLineDto[];
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface BankReconciliationListDto {
  id:                       string;
  reconciliationNumber:     string;
  bankAccountCode:          string;
  bankAccountName:          string;
  currencyCode:             string;
  statementDate:            string;
  statementOpeningBalance:  number;
  statementClosingBalance:  number;
  bookOpeningBalance:       number;
  clearedCredits:           number;
  clearedDebits:            number;
  bookClosingBalance:       number;
  unclearedCredits:         number;
  unclearedDebits:          number;
  difference:               number;
  status:                   ReconciliationStatus;
  completedDate?:           string | null;
  createdDate:              string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface BankReconciliationFilterDto {
  companyId?:          string;
  bankAccountId?:      string;
  reconciliationNumber?: string;
  status?:             string;
  statementDateFrom?:  string;
  statementDateTo?:    string;
  pageNumber?: number;
  pageSize?:           number;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateBankReconciliationRequest {
  locationId?:             string | null;
  bankAccountId:           string;
  statementDate:           string;
  statementOpeningBalance: number;
  statementClosingBalance: number;
  bookOpeningBalance:      number;
  notes?:                  string | null;
}

export interface UpdateBankReconciliationRequest {
  statementDate:           string;
  statementOpeningBalance: number;
  statementClosingBalance: number;
  bookOpeningBalance:      number;
  notes?:                  string | null;
}

export interface ClearReconciliationItemsRequest {
  lineIds: string[];
  cleared: boolean;
}

// ── Statement Import ───────────────────────────────────────────────────────────

export interface ParseStatementRequest {
  csvContent:              string;
  delimiter?:              string;
  hasHeader?:              boolean;
  dateColumnIndex?:        number;
  descriptionColumnIndex?: number;
  debitColumnIndex?:       number;
  creditColumnIndex?:      number;
}

export interface StatementLineDto {
  date:        string;
  description: string;
  debit:       number;
  credit:      number;
  amount:      number; // positive = credit (inflow), negative = debit (outflow)
}
